import { RemoteWidgetResponse } from 'api';

const loadExternalResource = (url: string, type: 'css' | 'js', integrity?: string): Promise<void> => {
  const selector = type === 'css' ? `link[href="${url}"]` : `script[src="${url}"]`;

  if (document.querySelector(selector)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    let element: HTMLLinkElement | HTMLScriptElement;

    if (type === 'css') {
      element = document.createElement('link');
      element.rel = 'stylesheet';
      element.href = url;
    } else {
      element = document.createElement('script');
      element.src = url;
      element.async = true;
    }

    if (integrity) {
      element.crossOrigin = 'anonymous';
      element.integrity = integrity;
    }

    element.onload = () => resolve();
    element.onerror = () => reject(new Error(`Failed to load ${url}`));

    const head = document.head;
    if (head) {
      head.appendChild(element);
      return;
    }
    reject(new Error('Document head is not available'));
  });
};

const applyRemoteWidgetState = (
  remoteWidget: RemoteWidgetResponse,
  widgetId: string,
  widgetName: string,
) => {
  window.INITIAL_STATE = {
    ...((window.INITIAL_STATE ?? {}) as Record<string, unknown>),
    [widgetId]: remoteWidget.state[widgetName],
  };
};

export const loadRemoteWidgetAssets = async (
  remoteWidget: RemoteWidgetResponse,
  widgetId: string,
  widgetName: string,
) => {
  applyRemoteWidgetState(remoteWidget, widgetId, widgetName);

  const styles = remoteWidget.styles.map((style) => ({
    url: style.props.href as string,
    integrity: style.props.integrity as string | undefined,
  }));

  const scripts = remoteWidget.scripts.map((script) => ({
    url: script.props.src as string,
    integrity: script.props.integrity as string | undefined,
  }));

  return Promise.all([loadStyles(styles), loadScripts(scripts)]);
};

const loadStyles = (styles: Array<{ url: string; integrity?: string }>): Promise<void> =>
  Promise.all(styles.map(({ url, integrity }) => loadExternalResource(url, 'css', integrity))).then(
    () => {},
  );

const loadScripts = (scripts: Array<{ url: string; integrity?: string }>): Promise<void> =>
  Promise.all(scripts.map(({ url, integrity }) => loadExternalResource(url, 'js', integrity))).then(
    () => {},
  );
